import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ChartConfiguration, ChartData } from 'chart.js';
import { ThemeService } from '../../shared/services/theme.service';
import { resolveChartAxisColors, resolveChartPalette } from '../../shared/chart-colors/chart-config';
import { ToolResultView } from '../agent-tool-result.model';

// Renders a tool result as a table, metric cards, or a line chart (built in agent-tool-result).
// Shown in the expanded tool pill in place of the raw JSON.
@Component({
  selector: 'app-agent-tool-result',
  templateUrl: './agent-tool-result.component.html',
  styleUrls: ['./agent-tool-result.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentToolResultComponent {
  readonly view = input.required<ToolResultView>();

  private readonly isDark = toSignal(inject(ThemeService).isDarkThemeBS, { initialValue: false });
  // Canvas can't read CSS variables, so the colours are re-resolved whenever dark mode flips.
  private readonly chartColors = computed(() => ({
    line: resolveChartPalette()[0],
    axis: resolveChartAxisColors(!!this.isDark()),
  }));

  // Split by kind so the template can narrow via `@if (x; as y)`.
  protected readonly table = computed(() => {
    const view = this.view();
    return view.kind === 'table' ? view : undefined;
  });
  protected readonly metrics = computed(() => {
    const view = this.view();
    return view.kind === 'metrics' ? view : undefined;
  });
  protected readonly chart = computed(() => {
    const view = this.view();
    return view.kind === 'chart' ? view : undefined;
  });
  protected readonly generic = computed(() => {
    const view = this.view();
    return view.kind === 'generic' ? view : undefined;
  });
  protected readonly sites = computed(() => {
    const view = this.view();
    return view.kind === 'sites' ? view : undefined;
  });

  protected readonly chartData = computed<ChartData<'line'>>(() => {
    const chart = this.chart();
    if (!chart) {
      return { labels: [], datasets: [] };
    }
    const color = this.chartColors().line;
    return {
      labels: chart.points.map((point) => point.label),
      datasets: [
        {
          data: chart.points.map((point) => point.value),
          borderColor: color,
          backgroundColor: 'transparent',
          borderWidth: 2,
          tension: 0.25,
          pointRadius: chart.points.length > 20 ? 0 : 2,
        },
      ],
    };
  });

  protected readonly chartOptions = computed<ChartConfiguration<'line'>['options']>(() => {
    const axis = this.chartColors().axis;
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        x: { ticks: { maxTicksLimit: 6, font: { size: 10 }, color: axis.text }, grid: { display: false } },
        y: {
          beginAtZero: true,
          ticks: { maxTicksLimit: 4, font: { size: 10 }, color: axis.text },
          grid: { color: axis.grid },
        },
      },
    };
  });
}
